// @ts-nocheck
import { useState } from 'react'
import { landingPlanPerks } from '../../lib/subscription-plans'
import { priceForCard, yearlyCadenceForLang, trialCopy, toggleCopy } from '../../lib/pricing'
import { BrandedText } from './public-data'

export default function LandingBelowFold({ lc, lcLang, onStart }) {
  const [period, setPeriod] = useState('monthly')
  const isYearly = period === 'yearly'
  const trial = trialCopy(lcLang)
  const toggle = toggleCopy(lcLang)

  const paidPlanId = { basic: 'starter', pro: 'author', publisher: 'pro' }
  const normalizePlan = (plan) => Array.isArray(plan)
    ? { id: plan[4], name: plan[0], price: plan[1], cadence: plan[2], desc: plan[3], featured: !!plan[5], perks: [] }
    : plan
  const plans = (lc.plans || []).map((plan) => {
    const normalized = normalizePlan(plan)
    const perkPlanId = paidPlanId[normalized.id] || normalized.id
    return { ...normalized, perks: landingPlanPerks(perkPlanId, lcLang) }
  })
  const startBook = () => onStart('pro')

  // Toggle pill styling (inline so it never touches global CSS / other components).
  const pillBtn = (active) => ({
    border: 'none',
    cursor: 'pointer',
    borderRadius: 999,
    padding: '8px 18px',
    fontSize: 14,
    fontWeight: 700,
    transition: 'background .2s, color .2s',
    background: active ? 'linear-gradient(135deg, var(--bbg-purple-dark), var(--bbg-rose))' : 'transparent',
    color: active ? '#fff' : 'var(--bbg-muted)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
  })

  return (
    <>
      <section className="bbg-section bbg-pricing bbg-pricing--spotlight" id="pricing">
        <div className="bbg-pricing-head">
          <div className="bbg-label bbg-label-gold">{lc.pricingLabel || 'PRICING'}</div>
          <h2 className="bbg-pricing-title">{lc.pricingTitle || 'Choose your author plan'}</h2>
          <p>{lc.pricingSub || 'Every plan includes books, covers, export, and 100% ownership. Upgrade anytime.'}</p>
        </div>

        {/* 7-day free trial banner — sits above the 3 plan cards */}
        <div
          style={{
            maxWidth: 680,
            margin: '0 auto 28px',
            border: '1px solid var(--bbg-purple)',
            borderRadius: 20,
            padding: '22px 26px',
            background: 'linear-gradient(135deg, rgba(124,58,237,0.16), rgba(240,165,200,0.08))',
            textAlign: 'center',
          }}
        >
          <span className="bbg-badge" style={{ marginBottom: 12 }}>{trial.badge}</span>
          <h3 style={{ color: 'var(--bbg-ink)', fontSize: 22, fontWeight: 700, margin: '0 0 8px', letterSpacing: '-0.02em' }}>
            <BrandedText>{trial.title}</BrandedText>
          </h3>
          <p style={{ color: 'var(--bbg-muted)', fontSize: 15, lineHeight: 1.55, margin: '0 auto 16px', maxWidth: 520 }}>
            {trial.desc}
          </p>
          <button type="button" className="bbg-btn bbg-btn-primary" onClick={() => onStart('basic')}>{trial.cta}</button>
        </div>

        {/* Monthly / Yearly toggle */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 32 }}>
          <div
            role="tablist"
            aria-label="Billing period"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: 4,
              borderRadius: 999,
              border: '1px solid var(--bbg-purple-light)',
              background: 'var(--bbg-surface)',
            }}
          >
            <button type="button" role="tab" aria-selected={!isYearly} style={pillBtn(!isYearly)} onClick={() => setPeriod('monthly')}>
              {toggle.monthly}
            </button>
            <button type="button" role="tab" aria-selected={isYearly} style={pillBtn(isYearly)} onClick={() => setPeriod('yearly')}>
              {toggle.yearly}
              <span style={{
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: '.04em',
                padding: '2px 7px',
                borderRadius: 999,
                background: isYearly ? 'rgba(255,255,255,0.22)' : 'var(--bbg-purple-pale)',
                color: isYearly ? '#fff' : 'var(--bbg-purple)',
              }}>{toggle.freeMonths}</span>
            </button>
          </div>
        </div>

        <div className="bbg-price-grid">
          {plans.map((plan) => {
            const pc = priceForCard(plan.id, lcLang, period)
            const cadence = isYearly ? yearlyCadenceForLang(lcLang) : plan.cadence
            return (
              <article className={`bbg-price-card ${plan.featured ? 'featured' : ''}`} key={plan.id}>
                {plan.featured ? <div className="bbg-badge">{lc.featured}</div> : null}
                <h3>{plan.name}</h3>
                <div className="bbg-price">{pc.price}<span>{cadence}</span></div>
                <div
                  aria-hidden={!isYearly}
                  style={{
                    color: 'var(--bbg-purple)',
                    fontSize: 13,
                    fontWeight: 700,
                    minHeight: 18,
                    marginBottom: 6,
                    opacity: isYearly ? 1 : 0,
                    transition: 'opacity .25s ease',
                  }}
                >
                  {pc.saveText}
                </div>
                <p className="bbg-price-desc">{plan.desc}</p>
                {plan.perks?.length ? (
                  <ul className="bbg-price-perks">
                    {plan.perks.map((perk) => <li key={perk}>{perk}</li>)}
                  </ul>
                ) : null}
                <button type="button" className="bbg-btn bbg-btn-primary" onClick={() => onStart(plan.id)}>{lc.choose}</button>
              </article>
            )
          })}
        </div>
      </section>

      <section className="bbg-section bbg-what">
        <div className="bbg-copy">
          <div className="bbg-label">{lc.whatLabel}</div>
          <h2>{lc.whatTitle}</h2>
          <ul className="bbg-check-list">
            {lc.bullets.map((item) => <li key={item}>{item}</li>)}
          </ul>
          <button className="bbg-link-btn" onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}>
            <BrandedText>{lc.howLink}</BrandedText>
          </button>
        </div>
      </section>

      <section className="bbg-section bbg-features">
        <div className="bbg-section-head">
          <h2><BrandedText>{lc.featuresTitle}</BrandedText></h2>
        </div>
        <div className="bbg-feature-grid">
          {lc.features.map(([icon, title, text]) => (
            <article className="bbg-feature-card" key={title}>
              <div className="bbg-feature-icon">{icon}</div>
              <h3>{title}</h3>
              <p><BrandedText>{text}</BrandedText></p>
            </article>
          ))}
        </div>
      </section>

      <section className="bbg-section bbg-how" id="how-it-works">
        <div className="bbg-section-head">
          <div className="bbg-label">{lc.howLabel}</div>
          <h2>{lc.howTitle}</h2>
        </div>
        <div className="bbg-step-grid">
          {lc.steps.map(([num, title, text]) => (
            <article className="bbg-step-card" key={num}>
              <span>{num}</span>
              <h3>{title}</h3>
              <p><BrandedText>{text}</BrandedText></p>
            </article>
          ))}
        </div>
        <div className="bbg-center">
          <button className="bbg-btn bbg-btn-primary" onClick={startBook}>{lc.writeCta}</button>
        </div>
      </section>

      <section className="bbg-section bbg-genres" id="genres">
        <div className="bbg-section-head">
          <h2>{lc.genresTitle}</h2>
        </div>
        <div className="bbg-genre-wrap">
          {lc.genreGroups.map(([group, items]) => (
            <div className="bbg-genre-group" key={group}>
              <h3>{group}</h3>
              <div>
                {items.map((item) => <span className="bbg-pill" key={item}>{item}</span>)}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bbg-section bbg-income">
        <div className="bbg-income-card">
          <div className="bbg-label bbg-label-gold">{lc.income.label}</div>
          <h2>{lc.income.title}</h2>
          <p><BrandedText>{lc.income.body}</BrandedText></p>
          <div className="bbg-income-points">
            {lc.income.points.map((point) => <span key={point}>✓ {point}</span>)}
          </div>
        </div>
      </section>

      <section className="bbg-final">
        <h2>{lc.finalTitle}</h2>
        <p>{lc.finalSub}</p>
        <button className="bbg-btn bbg-btn-primary" onClick={startBook}>{lc.writeCta}</button>
        <div className="bbg-final-trust">{lc.finalTrust}</div>
      </section>
    </>
  )
}
